/**
 * Concept maps from text: concepts joined by labelled linking phrases.
 *
 * Each line is a chain — `Plants -make-> Oxygen -is needed for-> Respiration` — whose
 * items are concepts and whose arrow labels are the linking phrases, so every arrow
 * reads as a sentence: *Plants make Oxygen*. A line that starts with an arrow continues
 * from the concept that began the line above. The first concept is the top of the map.
 *
 * Links that join two concepts already connected through the rest of the map are
 * cross-links, the part of a concept map Novak and Cañas single out as showing
 * connections between different branches. Each cluster's tree grows from its first
 * concept along the direction of the links; whatever link is left over is a cross-link.
 */

import { LINE_HEIGHT, type Rect } from "@/lib/tool-kit/charts/layout";
import {
  estimateTextWidth,
  maxTextWidth,
  type TextStyle,
  wrapText,
} from "@/lib/tool-kit/charts/text";
import {
  type ChainLine,
  nodeKey,
  parseChainLine,
} from "@/lib/tool-kit/diagrams-more/graph-text";
import {
  type LayeredDirection,
  layoutLayered,
  type PlacedEdge,
} from "@/lib/tool-kit/diagrams-more/layered";

export interface Concept {
  id: string;
  text: string;
  line: number;
}

export interface ConceptLink {
  from: string;
  to: string;
  phrase: string;
  line: number;
  crossLink: boolean;
}

export type ConceptWarningKind =
  | "selfLink"
  | "repeat"
  | "emptyConcept"
  | "noConceptAbove"
  | "tooMany";

export interface ConceptWarning {
  kind: ConceptWarningKind;
  line: number;
  text: string;
}

export interface ConceptParse {
  concepts: Concept[];
  links: ConceptLink[];
  warnings: ConceptWarning[];
  /** Links written without a linking phrase. */
  unlabelled: number;
}

const LINE_BREAK = /\r\n|\r|\n/;

interface Draft {
  concepts: Concept[];
  byKey: Map<string, number>;
  links: ConceptLink[];
  warnings: ConceptWarning[];
  limit: number;
  linkLimit: number;
}

function conceptIndex(draft: Draft, raw: string, line: number): number | null {
  const text = raw.trim();
  const key = nodeKey(text);
  const known = draft.byKey.get(key);
  if (known !== undefined) {
    return known;
  }
  if (draft.concepts.length >= draft.limit) {
    draft.warnings.push({ kind: "tooMany", line, text });
    return null;
  }
  draft.concepts.push({ id: `c${draft.concepts.length}`, text, line });
  draft.byKey.set(key, draft.concepts.length - 1);
  return draft.concepts.length - 1;
}

function addLink(
  draft: Draft,
  from: number,
  to: number,
  phrase: string,
  line: number
): void {
  const a = draft.concepts[from];
  const b = draft.concepts[to];
  if (from === to) {
    draft.warnings.push({ kind: "selfLink", line, text: a.text });
    return;
  }
  if (draft.links.some((link) => link.from === a.id && link.to === b.id)) {
    draft.warnings.push({
      kind: "repeat",
      line,
      text: `${a.text} → ${b.text}`,
    });
    return;
  }
  if (draft.links.length >= draft.linkLimit) {
    draft.warnings.push({
      kind: "tooMany",
      line,
      text: `${a.text} → ${b.text}`,
    });
    return;
  }
  draft.links.push({
    from: a.id,
    to: b.id,
    phrase,
    line,
    crossLink: false,
  });
}

/**
 * Mark every link that is not part of a spanning forest. Each cluster's tree grows from
 * its first concept following the direction of the links, so a link that continues the
 * hierarchy is never the one left over; a concept that no arrow reaches is attached by
 * the first link that touches it.
 */
export function markCrossLinks(
  concepts: readonly Concept[],
  links: ConceptLink[]
): void {
  const seen = new Set<string>();
  const tree = new Set<ConceptLink>();
  const grow = (root: string) => {
    const queue = [root];
    for (const id of queue) {
      for (const link of links) {
        if (link.from === id && !seen.has(link.to)) {
          seen.add(link.to);
          tree.add(link);
          queue.push(link.to);
        }
      }
    }
  };
  const attachOne = (): boolean => {
    for (const link of links) {
      const fromSeen = seen.has(link.from);
      if (fromSeen !== seen.has(link.to)) {
        const added = fromSeen ? link.to : link.from;
        seen.add(added);
        tree.add(link);
        grow(added);
        return true;
      }
    }
    return false;
  };
  for (const start of concepts) {
    if (seen.has(start.id)) {
      continue;
    }
    seen.add(start.id);
    grow(start.id);
    while (attachOne()) {
      // Keep attaching until the cluster is spanned.
    }
  }
  for (const link of links) {
    link.crossLink = !tree.has(link);
  }
}

function readLine(
  draft: Draft,
  chain: ChainLine,
  line: number,
  anchor: number | null
): number | null {
  const ids = chain.items.map((item) => conceptIndex(draft, item, line));
  const heads: (number | null)[] = chain.lead ? [anchor, ...ids] : ids;
  const phrases = chain.lead
    ? [chain.lead.label, ...chain.arrows.map((arrow) => arrow.label)]
    : chain.arrows.map((arrow) => arrow.label);
  for (let i = 0; i + 1 < heads.length; i++) {
    const [from, to] = [heads[i], heads[i + 1]];
    if (from !== null && to !== null) {
      addLink(draft, from, to, phrases[i] ?? "", line);
    }
  }
  return chain.lead ? anchor : (ids.at(-1) ?? null);
}

/** Read the map. Concepts or links beyond the limits are left out with a warning. */
export function parseConceptMap(
  text: string,
  limit = 50,
  linkLimit = 90
): ConceptParse {
  const draft: Draft = {
    concepts: [],
    byKey: new Map(),
    links: [],
    warnings: [],
    limit,
    linkLimit,
  };
  let anchor: number | null = null;
  for (const [index, raw] of text.split(LINE_BREAK).entries()) {
    const line = index + 1;
    const chain = parseChainLine(raw);
    if (chain.empty > 0 || (chain.items.length === 0 && chain.lead)) {
      draft.warnings.push({ kind: "emptyConcept", line, text: raw.trim() });
    }
    if (chain.items.length === 0) {
      continue;
    }
    if (chain.lead && anchor === null) {
      draft.warnings.push({ kind: "noConceptAbove", line, text: raw.trim() });
      continue;
    }
    anchor = readLine(draft, chain, line, anchor);
  }
  markCrossLinks(draft.concepts, draft.links);
  return {
    concepts: draft.concepts,
    links: draft.links,
    warnings: draft.warnings,
    unlabelled: draft.links.filter((link) => link.phrase === "").length,
  };
}

/* ------------------------------------------------------------------ layout */

export const CONCEPT_FONT = 13;
export const PHRASE_FONT = 12;
const CONCEPT_STYLE: TextStyle = { fontSize: CONCEPT_FONT };
const PHRASE_STYLE: TextStyle = { fontSize: PHRASE_FONT };
const CONCEPT_LINE = CONCEPT_FONT * LINE_HEIGHT;
const PHRASE_LINE = PHRASE_FONT * LINE_HEIGHT;

export interface PlacedConcept {
  concept: Concept;
  lines: string[];
  rect: Rect;
}

export interface PlacedPhrase {
  link: ConceptLink;
  path: PlacedEdge;
  lines: string[];
}

export interface ConceptLayout {
  width: number;
  height: number;
  concepts: PlacedConcept[];
  links: PlacedPhrase[];
}

function conceptSize(text: string): {
  lines: string[];
  width: number;
  height: number;
} {
  const lines = wrapText(text, 140, CONCEPT_STYLE, 3);
  return {
    lines,
    width: Math.max(72, Math.ceil(maxTextWidth(lines, CONCEPT_STYLE)) + 24),
    height: Math.max(34, Math.ceil(lines.length * CONCEPT_LINE) + 16),
  };
}

function phraseLines(phrase: string): string[] {
  return phrase === "" ? [] : wrapText(phrase, 112, PHRASE_STYLE, 3);
}

/** Lay the parsed map out. */
export function layoutConceptMap(
  parse: ConceptParse,
  direction: LayeredDirection
): ConceptLayout {
  const sizes = parse.concepts.map((concept) => conceptSize(concept.text));
  const phrases = parse.links.map((link) => phraseLines(link.phrase));
  const layout = layoutLayered(
    parse.concepts.map((concept, i) => ({
      id: concept.id,
      width: sizes[i].width,
      height: sizes[i].height,
    })),
    parse.links.map((link, i) => ({
      from: link.from,
      to: link.to,
      label:
        phrases[i].length === 0
          ? null
          : {
              width:
                Math.ceil(
                  Math.max(
                    ...phrases[i].map((line) =>
                      estimateTextWidth(line, PHRASE_STYLE)
                    )
                  )
                ) + 12,
              height: Math.ceil(phrases[i].length * PHRASE_LINE) + 6,
            },
    })),
    { direction, nodeGap: direction === "down" ? 30 : 22 }
  );
  const rects = new Map(layout.nodes.map((node) => [node.id, node]));
  return {
    width: layout.width,
    height: layout.height,
    concepts: parse.concepts.map((concept, i) => {
      const placed = rects.get(concept.id) as Rect;
      return {
        concept,
        lines: sizes[i].lines,
        rect: {
          x: placed.x,
          y: placed.y,
          width: placed.width,
          height: placed.height,
        },
      };
    }),
    links: layout.edges.map((path) => ({
      link: parse.links[path.index],
      path,
      lines: phrases[path.index],
    })),
  };
}

/* ------------------------------------------------------- text alternative */

export interface ConceptRow {
  from: string;
  phrase: string;
  to: string;
  crossLink: boolean;
}

export function conceptRows(parse: ConceptParse): ConceptRow[] {
  const byId = new Map(parse.concepts.map((concept) => [concept.id, concept]));
  return parse.links.map((link) => ({
    from: byId.get(link.from)?.text ?? "",
    phrase: link.phrase,
    to: byId.get(link.to)?.text ?? "",
    crossLink: link.crossLink,
  }));
}

/** Each link written as the sentence it reads as: `Plants make Oxygen`. */
export function conceptSentences(parse: ConceptParse): string {
  return conceptRows(parse)
    .map((row) => [row.from, row.phrase, row.to].filter(Boolean).join(" "))
    .join("\n");
}

export function conceptCounts(parse: ConceptParse): {
  concepts: number;
  links: number;
  crossLinks: number;
} {
  return {
    concepts: parse.concepts.length,
    links: parse.links.length,
    crossLinks: parse.links.filter((link) => link.crossLink).length,
  };
}
